import { useEffect, useState } from "react";

import { UploadBox, UploadBoxProps, Modal, Typography, InfoPanel, DocumentListItem } from "@king-ict/eupisi-ui/components";
import { DocumentData } from "@king-ict/eupisi-ui/models";
import { isObjectDuplicateInList } from "@king-ict/eupisi-ui/utils/objectUtils";
import { useTranslation } from "react-i18next";
  
export interface DocumentsUploadModalProps  {    
    title?: string | null;
    infoPanelText?: string | null;
    open: boolean;
    onConfirm: Function;
    onClose: Function;
    allDocuments: DocumentData[];
    onRemoveUploadedDocument: Function;
    hideOldDocuments?: boolean;
    btnOkText?: string;
    btnCancelText?: string;    
    maxSize: number;
    types: string;
    info?: string;
    maxFiles?: number;
    filesAction: ((files: File[]) => void) | undefined;
}



export const DocumentsUploadModal = ({
    title,
    infoPanelText,
    open,
    onConfirm,
    onClose,    
    allDocuments,   
    hideOldDocuments,
    maxSize,
    types,
    info,
    maxFiles,
    btnOkText,
    btnCancelText,    
    filesAction=undefined,
    onRemoveUploadedDocument
}: DocumentsUploadModalProps) => {

    const [documents, setDocuments] = useState<DocumentData[]>([]);

    const { t } = useTranslation();

    const resetForm = () => {
        setDocuments([]);
        onClose();
    };

    const confirmForm = (event?: any, newFiles?: Array<DocumentData>) => {
        const newDocuments: DocumentData[] = newFiles ? [...newFiles] : [...documents];
        setDocuments([]);
        onConfirm(newDocuments);
    };

    const DocumentsUploadModalTitle = () => {
        if(title === null) return null;

        return (
            <Typography variant="baseBold">
                {title || t("documentsUploadModal.modalTitle")}
            </Typography>            
        );
    };

    const DocumentsUploadModalInfoPanel = () => {
        if(infoPanelText === null) return null;

        return (
            <InfoPanel
                message={infoPanelText || t("documentsUploadModal.infoMessage")}
                color="success"
                fullWidth
            />
        );
    };


    const removeDocument = (document: DocumentData) => {
        const newFiles: DocumentData[] = [...documents];

        var removeDocumentIndex = newFiles.findIndex((file) => file.fileName === document.fileName);

        if(removeDocumentIndex !== -1) {
            newFiles.splice(removeDocumentIndex, 1);
            setDocuments(newFiles);
        }
    };

    const UploadedDocuments = () => (
        <>
            <Typography variant="baseBold">
                {t("documentsUploadModal.uploadedDocuments")}                
            </Typography>

            {documents.map((file, index) => (
                <DocumentListItem
                    key={index}
                    document={file}
                    className=""
                    onRemoveClick={() => removeDocument(file)}
                />                
            ))}
        </>
    );

    const handleFilesAction = (files: File[]) => {        
        const newFiles: DocumentData[] = [...documents];
        
        files.forEach((file: File) => {
            const document = {
                id: -1,
                fileName: file.name,
                file: file
            } as DocumentData;

            if (!isObjectDuplicateInList(document, newFiles)) {
                newFiles.push(document);
            }
        });

        setDocuments(newFiles);
    };

    const PreviouslyUploadedDocuments = () => {
        if(hideOldDocuments){
            return null;
        } 
        return (
            <>
                <Typography variant="baseBold">
                    {t("documentsUploadModal.previouslyUploadedDocuments")}
                </Typography>
                {allDocuments && allDocuments.length > 0 && (
                    <InfoPanel
                        message={t("documentsUploadModal.previouslyUploadedDocumentsInfo")}
                        color="success"
                        fullWidth
                    />
                )}                
                {allDocuments && allDocuments.map((document, index) => (
                    <DocumentListItem
                        key={index}
                        document={document}
                        onRemoveClick={() => onRemoveUploadedDocument(index)}                   
                    />
                ))}
            </>
        );
    };


    return(
        <Modal
            type={"confirm"}
            open={open}
            onClose={resetForm}
            onConfirm={confirmForm}
            confirmText={btnOkText || t("common.save")}
            cancelText={btnCancelText || t("common.cancel")}
            variant="big"
        >
            <div>
                <DocumentsUploadModalTitle />

                <DocumentsUploadModalInfoPanel />

                <UploadBox
                    filesAction={filesAction || handleFilesAction}
                    info={info || t("documentsUploadModal.uploadBoxInfo").replace('${mb}', (maxSize ? maxSize.toString() : '')).replace('${formats}', types)}
                    maxSize={maxSize}
                    types={types}
                    maxFiles={maxFiles}
                />
            </div>

            <UploadedDocuments />

            <br />

            <PreviouslyUploadedDocuments/>



        </Modal>
    );
};